/**
 * Centralized helpers for detecting and propagating quota-exhausted errors
 * coming from edge functions (HTTP 403 with code "QUOTA_EXHAUSTED" or a
 * "Quota épuisé" message).
 *
 * Quota errors should NEVER blank the screen — they are surfaced through
 * the QuotaErrorBoundary (synchronous render errors) or via the global
 * "quota-exhausted" CustomEvent (async / out-of-render errors).
 */

export interface QuotaErrorDetails {
  message: string;
  quotaType?: string;
  /** Optional retry callback the boundary/toast can offer to the user. */
  onRetry?: () => void;
}

export class QuotaExhaustedError extends Error {
  code = "QUOTA_EXHAUSTED" as const;
  status = 403;
  quotaType?: string;
  onRetry?: () => void;

  constructor(details: QuotaErrorDetails) {
    super(details.message || "Quota épuisé");
    this.name = "QuotaExhaustedError";
    this.quotaType = details.quotaType;
    this.onRetry = details.onRetry;
  }
}

/** Returns true if the error originates from an exhausted server-side quota. */
export function isQuotaError(error: unknown): boolean {
  if (!error) return false;
  const e = error as any;
  if (e instanceof QuotaExhaustedError) return true;
  if (e?.code === "QUOTA_EXHAUSTED") return true;
  if (e?.status === 403 && /quota/i.test(String(e?.message ?? ""))) return true;
  if (typeof e?.message === "string" && /quota\s+épuisé/i.test(e.message)) return true;
  return false;
}

/**
 * Extracts the user-facing message from a quota error. Always falls back to a
 * sensible French default — the UI must never show "undefined".
 */
export function getQuotaMessage(error: unknown): string {
  const e = error as any;
  return (
    e?.message ||
    e?.error ||
    "Quota épuisé. Achetez un pack de crédits pour continuer."
  );
}

const QUOTA_EVENT = "quota-exhausted";

/**
 * Soft-navigate to the quotas page WITHOUT a full page reload.
 * `window.location.assign` tears down the React tree mid-stream and can
 * leave a blank screen — pushState + popstate lets React Router handle it.
 */
export function goToQuotasPage(): void {
  if (typeof window === "undefined") return;
  const target = "/settings?tab=quotas";
  const router = (window as any).__appRouter;
  if (router?.navigate) {
    // TanStack Router owns history: a raw pushState + popstate desyncs it and
    // can unmount the whole tree (blank screen).
    Promise.resolve(router.navigate({ to: "/settings", search: { tab: "quotas" } }))
      .catch(() => window.location.assign(target));
    return;
  }
  window.location.assign(target);
}


/**
 * Broadcasts a quota error so the top-level QuotaErrorBoundary can react
 * even when the error happens outside of React's render cycle (fetch,
 * async handlers, edge function invocations).
 */
export function emitQuotaError(error: unknown, onRetry?: () => void): void {
  if (typeof window === "undefined") return;
  const e = error as any;
  window.dispatchEvent(
    new CustomEvent<QuotaErrorDetails>(QUOTA_EVENT, {
      detail: {
        message: getQuotaMessage(error),
        quotaType: e?.quotaType,
        onRetry,
      },
    })
  );
}

export function onQuotaError(
  handler: (details: QuotaErrorDetails) => void
): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (ev: Event) => handler((ev as CustomEvent<QuotaErrorDetails>).detail);
  window.addEventListener(QUOTA_EVENT, listener);
  return () => window.removeEventListener(QUOTA_EVENT, listener);
}
